import type { ReactNode } from "react";
import type { ResearchRequirementProjection, ResearchRunMode } from "@roboscientist/contracts";
import { FileText, Flag, Infinity, LockKeyhole, Paperclip, ShieldCheck, Target } from "lucide-react";
import "../requirement.css";

const MODE_PRESENTATION: Record<ResearchRunMode, { label: string; description: string; icon: typeof Flag }> = {
  bounded: {
    label: "Bounded task",
    description: "Finish when this requirement and its deliverables are satisfied.",
    icon: Flag
  },
  "persistent-goal": {
    label: "Persistent goal",
    description: "A native Goal carries the work across follow-up turns.",
    icon: Target
  },
  "continuous-improvement": {
    label: "Continuous",
    description: "Keep improving until the human explicitly pauses or clears the Goal.",
    icon: Infinity
  }
};

const notSpecified = (value: string): boolean => value.trim().toUpperCase() === "NA";

function RequirementValue({ value }: { value: string }) {
  return notSpecified(value)
    ? <p className="requirement-value requirement-value--na"><code>NA</code><span>Not specified</span></p>
    : <p className="requirement-value">{value}</p>;
}

function RequirementField({ label, value, prominent = false }: {
  label: string;
  value: string;
  prominent?: boolean;
}) {
  return <section className={"requirement-field" + (prominent ? " requirement-field--prominent" : "")}>
    <h3>{label}</h3>
    <RequirementValue value={value} />
  </section>;
}

function RequirementMeta({ label, children }: { label: string; children: ReactNode }) {
  return <div><dt>{label}</dt><dd>{children}</dd></div>;
}

export function ResearchRequirementView({
  requirement,
  source = "cockpit"
}: {
  requirement: ResearchRequirementProjection | null | undefined;
  source?: "cockpit" | "gallery";
}) {
  if (!requirement) return <section className="requirement-unavailable">
    <span><FileText size={20} /></span>
    <div>
      <strong>Confirmed Requirement unavailable</strong>
      <p>{source === "gallery"
        ? "This legacy snapshot predates structured Requirement capture. No content has been inferred."
        : "This research Topic was not created from a persisted Requirement intake. No content has been inferred."}</p>
    </div>
  </section>;

  const { draft } = requirement;
  const mode = MODE_PRESENTATION[requirement.runMode];
  const ModeIcon = mode.icon;
  return <article className="requirement-sheet" aria-label={`Confirmed Requirement for ${draft.topic}`}>
    <header className="requirement-sheet__header">
      <div className="requirement-sheet__identity">
        <span className="requirement-sheet__mark"><ShieldCheck size={20} /></span>
        <div>
          <span className="panel__eyebrow">{source === "gallery" ? "CAPTURED REQUIREMENT" : "CONFIRMED REQUIREMENT"}</span>
          <h2>{draft.topic}</h2>
        </div>
      </div>
      <div className="requirement-sheet__badges">
        <span className="requirement-badge requirement-badge--frozen"><LockKeyhole size={12} /> Frozen</span>
        <span className="requirement-badge"><ModeIcon size={12} /> {mode.label}</span>
      </div>
    </header>

    <div className="requirement-sheet__meta">
      <dl>
        <RequirementMeta label="Revision">v{requirement.revision}</RequirementMeta>
        <RequirementMeta label="Confirmed">{new Date(requirement.confirmedAt).toLocaleString()}</RequirementMeta>
        <RequirementMeta label="Integrity"><code>{requirement.sha256.slice(0, 12)}</code></RequirementMeta>
        {requirement.experimentId ? <RequirementMeta label="Experiment"><code>{requirement.experimentId}</code></RequirementMeta> : null}
      </dl>
    </div>

    <div className="requirement-sheet__body">
      <div className="requirement-content">
        <RequirementField label="Objective" value={draft.objective} prominent />

        <section className="requirement-section">
          <header><span>Decision standard</span><h3>What will count as an answer</h3></header>
          <div className="requirement-field-grid requirement-field-grid--decision">
            <RequirementField label="Evaluation & acceptance" value={draft.evaluation} />
            <RequirementField label="Baseline" value={draft.baseline} />
          </div>
        </section>

        <section className="requirement-section">
          <header><span>Boundaries</span><h3>Where the research may operate</h3></header>
          <div className="requirement-field-grid">
            <RequirementField label="Scope" value={draft.scope} />
            <RequirementField label="Constraints" value={draft.constraints} />
            <RequirementField label="Notes" value={draft.notes} />
          </div>
        </section>

        <section className="requirement-section">
          <header><span>Finish line</span><h3>What must be delivered and when to stop</h3></header>
          <div className="requirement-field-grid requirement-field-grid--finish">
            <RequirementField label="Deliverables" value={draft.deliverables} />
            <RequirementField label="Stop conditions" value={draft.stopConditions} />
          </div>
        </section>
      </div>

      <aside className="requirement-envelope">
        <header><span>EXECUTION ENVELOPE</span><h3>{mode.label}</h3><p>{mode.description}</p></header>
        <RequirementField label="Maximum GPUs" value={draft.maxGpus} />
        <RequirementField label="Runtime / budget" value={draft.computeBudget} />
        <RequirementField label="Environment / resources" value={draft.computeNotes} />
        <section className="requirement-sources">
          <h3><Paperclip size={13} /> Requirement sources</h3>
          {requirement.sourceFiles.length
            ? <ul>{requirement.sourceFiles.map((file) => <li key={file}><FileText size={12} /><span>{file}</span></li>)}</ul>
            : <p>No additional source files.</p>}
        </section>
        <p className="requirement-frozen-note"><LockKeyhole size={13} /> This Requirement is immutable. Runtime context may evolve without rewriting it.</p>
      </aside>
    </div>
  </article>;
}
